import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Eye, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { documentTypeToBadgeVariant } from '@/lib/clinicalBadges';
import { downloadBlob } from '@/lib/downloadFile';
import { formatDate } from '@/lib/format';
import { notifyApiError } from '@/lib/notifyApiError';
import type { DocumentViewModel } from '@/features/patient-record/types';

import { buildAttachmentDownloadUrl } from './attachmentUrl';
import { isPreviewable } from './previewable';

interface DocumentRowProps {
  readonly document: DocumentViewModel;
  readonly onView: () => void;
  readonly onDelete: () => void;
}

export function DocumentRow({
  document,
  onView,
  onDelete,
}: DocumentRowProps): ReactElement {
  const { t } = useTranslation();
  const previewable = isPreviewable(document.fileName);
  const download = (): void => {
    downloadBlob(
      buildAttachmentDownloadUrl(document.id),
      document.fileName ?? document.title,
    ).catch(notifyApiError);
  };

  return (
    <div className="flex items-start gap-3">
      <Badge
        variant={documentTypeToBadgeVariant(document.documentType)}
        className="text-[10px] h-5 shrink-0 uppercase tracking-wide"
      >
        {t(`patient.documents.type.${document.documentType}`)}
      </Badge>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-card-foreground truncate">
          {document.title}
        </p>
        <p className="text-xs text-muted-foreground">
          {formatDate(document.createdAt)}
          {document.createdBy !== null && (
            <>
              {' · '}
              {t('patient.documents.uploadedBy', { value: document.createdBy })}
            </>
          )}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {previewable && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onView}
            aria-label={t('patient.documents.view')}
            title={t('patient.documents.view')}
          >
            <Eye className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={download}
          title={t('patient.documents.download')}
          aria-label={t('patient.documents.download')}
        >
          <Download className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          aria-label={t('patient.documents.delete')}
        >
          <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
